import { useEffect, useState } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';

interface PaymentSuccessPageProps {
  planName?: string;
  onNavigate?: (view: string) => void;
}

export default function PaymentSuccessPage({
  planName = 'Delegate Replica',
  onNavigate,
}: PaymentSuccessPageProps) {
  const [visible, setVisible] = useState(false);
  const [ticketVisible, setTicketVisible] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 100);
    const t2 = setTimeout(() => setTicketVisible(true), 500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div
      style={{ backgroundColor: '#1a1816', minHeight: '100vh', fontFamily: "'Georgia', 'Times New Roman', serif" }}
      className="flex flex-col items-center justify-center relative overflow-hidden"
    >
      {/* Subtle grain overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E")`,
          opacity: 0.6,
        }}
      />

      {/* Subtle radial glow in center */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: '800px',
          height: '500px',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -60%)',
          background: 'radial-gradient(ellipse at center, rgba(200, 160, 110, 0.06) 0%, transparent 70%)',
        }}
      />

      {/* Main Content */}
      <div
        className="relative z-10 flex flex-col items-center text-center px-6"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(16px)',
          transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}
      >
        {/* Title */}
        <h1
          style={{
            color: '#f5f0e8',
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: 400,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            maxWidth: '900px',
            marginBottom: '1rem',
          }}
        >
          Votre passeport pour accéder<br />
          à votre <em style={{ fontStyle: 'italic' }}>réplique numérique</em>
        </h1>

        {/* Subtitle */}
        <p
          style={{
            color: 'rgba(245, 240, 232, 0.45)',
            fontSize: '1rem',
            letterSpacing: '0.01em',
            marginBottom: '3.5rem',
            fontFamily: "'Inter', system-ui, sans-serif",
            fontWeight: 400,
          }}
        >
          Vous venez d'obtenir l'accès à HumanReplica® — {planName}
        </p>

        {/* ─────── TICKET ─────── */}
        <div
          style={{
            opacity: ticketVisible ? 1 : 0,
            transform: ticketVisible ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(20px)',
            transition: 'opacity 0.7s ease, transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          <Ticket planName={planName} />
        </div>

        {/* CTA Button */}
        <div
          style={{
            marginTop: '2.5rem',
            opacity: ticketVisible ? 1 : 0,
            transform: ticketVisible ? 'translateY(0)' : 'translateY(10px)',
            transition: 'opacity 0.6s ease 0.3s, transform 0.6s ease 0.3s',
          }}
        >
          <button
            onClick={() => onNavigate?.('dashboard')}
            style={{
              backgroundColor: 'transparent',
              border: '1.5px solid rgba(245, 240, 232, 0.35)',
              color: '#f5f0e8',
              padding: '0.75rem 2rem',
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontFamily: "'Inter', system-ui, sans-serif",
              fontWeight: 500,
              letterSpacing: '0.02em',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'border-color 0.2s, background 0.2s',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(245, 240, 232, 0.7)';
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(245, 240, 232, 0.05)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(245, 240, 232, 0.35)';
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            Accéder à mon tableau de bord
            <ArrowRight size={15} />
          </button>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 flex flex-col items-center"
        style={{
          color: 'rgba(245,240,232,0.3)',
          opacity: ticketVisible ? 1 : 0,
          transition: 'opacity 0.6s ease 0.8s',
        }}
      >
        <ChevronDown size={20} className="animate-bounce" />
      </div>
    </div>
  );
}

/* ─────── Ticket Sub-component ─────── */

function Ticket({ planName }: { planName: string }) {
  const ticketBg = '#faf6ee';
  const borderColor = '#c8b89a';
  const textDark = '#2c2218';
  const textMuted = '#9a7d5e';
  const accentOrange = '#c77a4c';

  return (
    <div
      style={{
        position: 'relative',
        width: '480px',
        maxWidth: '90vw',
      }}
    >
      {/* Ticket body */}
      <div
        style={{
          backgroundColor: ticketBg,
          border: `1.5px solid ${borderColor}`,
          borderRadius: '8px',
          position: 'relative',
          overflow: 'visible',
        }}
      >
        {/* Dashed top border inside */}
        <div style={{ padding: '0 20px' }}>
          <div style={{ height: '1px', borderTop: `1.5px dashed ${borderColor}`, margin: '30px 0 0' }} />
        </div>

        {/* Top metadata row */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '8px 24px',
            fontSize: '0.55rem',
            fontFamily: "'Inter', monospace, sans-serif",
            fontWeight: 600,
            letterSpacing: '0.12em',
            color: textMuted,
            textTransform: 'uppercase',
          }}
        >
          <span>ADMIT ONE</span>
          <span>⬥</span>
          <span>VALID FOR: {planName.toUpperCase()}</span>
        </div>

        {/* Center content */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: '24px 40px 20px',
            position: 'relative',
          }}
        >
          {/* Left side text (rotated) */}
          <div
            style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%) rotate(180deg)',
              fontSize: '0.55rem',
              fontFamily: 'monospace',
              fontWeight: 600,
              letterSpacing: '0.15em',
              color: textMuted,
              writingMode: 'vertical-rl',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            0680801 · CLICK HERE
          </div>

          {/* Right side text (rotated) */}
          <div
            style={{
              position: 'absolute',
              right: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.55rem',
              fontFamily: 'monospace',
              fontWeight: 600,
              letterSpacing: '0.15em',
              color: textMuted,
              writingMode: 'vertical-rl',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            0680801 · CLICK HERE
          </div>

          {/* Logo mark */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
            {/* HumanReplica® symbol – stylized HR */}
            <HRSymbol color={accentOrange} />
            <div style={{ textAlign: 'left' }}>
              <div
                style={{
                  fontSize: '1.8rem',
                  fontWeight: 700,
                  color: textDark,
                  lineHeight: 1,
                  fontFamily: "'Georgia', serif",
                  letterSpacing: '-0.02em',
                }}
              >
                HumanReplica®
              </div>
              <div
                style={{
                  fontSize: '0.9rem',
                  color: accentOrange,
                  fontStyle: 'italic',
                  fontFamily: "'Georgia', serif",
                  marginTop: '2px',
                }}
              >
                Immediate access
              </div>
            </div>
          </div>

          {/* Icon decoration */}
          <div style={{ marginTop: '12px' }}>
            <ReplicaIcon color={accentOrange} />
          </div>
        </div>

        {/* Bottom metadata row */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '8px 24px',
            fontSize: '0.55rem',
            fontFamily: "'Inter', monospace, sans-serif",
            fontWeight: 600,
            letterSpacing: '0.12em',
            color: textMuted,
            textTransform: 'uppercase',
            transform: 'rotate(180deg)',
          }}
        >
          <span>ADMIT ONE</span>
          <span>⬥</span>
          <span>VALID FOR: {planName.toUpperCase()}</span>
        </div>

        {/* Dashed bottom border inside */}
        <div style={{ padding: '0 20px' }}>
          <div style={{ height: '1px', borderTop: `1.5px dashed ${borderColor}`, marginBottom: '30px' }} />
        </div>

        {/* Left notch cutout */}
        <div
          style={{
            position: 'absolute',
            left: '-14px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            background: '#1a1816',
            border: `1.5px solid ${borderColor}`,
            zIndex: 10,
          }}
        />

        {/* Right notch cutout */}
        <div
          style={{
            position: 'absolute',
            right: '-14px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            background: '#1a1816',
            border: `1.5px solid ${borderColor}`,
            zIndex: 10,
          }}
        />
      </div>
    </div>
  );
}

/* ─────── SVG Icons ─────── */

function HRSymbol({ color }: { color: string }) {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Asterisk / starburst – 8 lines radiating from center */}
      {Array.from({ length: 8 }, (_, i) => {
        const angle = (i * 45 * Math.PI) / 180;
        const cx = 22;
        const cy = 22;
        const r1 = 5;
        const r2 = 18;
        return (
          <line
            key={i}
            x1={cx + r1 * Math.cos(angle)}
            y1={cy + r1 * Math.sin(angle)}
            x2={cx + r2 * Math.cos(angle)}
            y2={cy + r2 * Math.sin(angle)}
            stroke={color}
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
}

function ReplicaIcon({ color }: { color: string }) {
  return (
    <svg width="36" height="24" viewBox="0 0 36 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Simplified "replica" pixel art icon – two rows of blocks */}
      {[0, 4, 8].map(x => (
        <rect key={`t${x}`} x={x + 10} y={0} width="3" height="8" rx="0.5" fill={color} />
      ))}
      {[0, 4, 8, 12].map(x => (
        <rect key={`b${x}`} x={x + 8} y={10} width="3" height="6" rx="0.5" fill={color} />
      ))}
    </svg>
  );
}
